import { ReactNode } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import { Autocomplete, AutocompleteRenderGroupParams, Divider, FormControl, FormLabel, List, Stack, TextField, Typography } from '@mui/material';

import { FieldGroupedOption } from '../types/FormField';

export interface AutocompleteFieldProps {
  label: string;
  disabled: boolean;
  value?: string;
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly?: boolean;
  groupedOptions: FieldGroupedOption[];
  disabledOptions?: (string | undefined)[];
}

export const AutocompleteField = ({
  label,
  disabled,
  value = '',
  onChange,
  error,
  name,
  readOnly = false,
  groupedOptions,
  disabledOptions = [],
}: AutocompleteFieldProps) => {
  const renderAutocompleteGroup = (params: AutocompleteRenderGroupParams) => {
    // Check if we are iterating over the last group, in that case we don't show the divider
    const { children, key, group } = params as unknown as { children: ReactNode[]; key: string; group: string };
    const showDivider = options.length > parseInt(key) + children.length;
    return (
      <li key={key}>
        <Typography component={'p'} variant="caption" px={2} py={1}>
          {group}
        </Typography>
        <List sx={{ p: 0 }}>{children}</List>
        {showDivider && <Divider sx={{ mx: 0, my: 1 }} />}
      </li>
    );
  };

  const options: { label: string; group: string; value: string }[] = groupedOptions.flatMap((groupedOption) =>
    groupedOption.options.map((option) => ({ group: groupedOption.group, label: option.label || option.value, value: option.value })),
  );

  return (
    <>
      <FormControl fullWidth size="small">
        <FormLabel id={`${name}-label`}>{label}</FormLabel>
        <Stack spacing={2}>
          <Autocomplete
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.group}
            getOptionDisabled={(option) => Boolean(disabledOptions.find((disabledOption) => disabledOption === option.label))}
            renderGroup={renderAutocompleteGroup}
            disableClearable
            disabled={disabled}
            aria-labelledby={`${name}-label`}
            size="small"
            options={options}
            value={options.find((o) => o.label === value)}
            renderTags={() => null}
            renderInput={(params) => {
              return <TextField {...params} error={!!error} aria-invalid={!!error} placeholder="Select option" />;
            }}
            onChange={(_, v) => onChange(v.value)}
            readOnly={readOnly}
            slotProps={{ listbox: { sx: { p: 0 } } }}
          />
        </Stack>
      </FormControl>
      {error && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </>
  );
};
